/**
 * Shared API-error helpers for the cabinet (AdminClient) portal.
 *
 * #RELATIONSHIPS-react31 root cause: FastAPI / Pydantic-v2 validation (422)
 * responses return `detail` as an ARRAY of `{ type, loc, msg, input, ctx }`
 * objects. Passing that array straight to `toast.error(...)` or rendering it as
 * a React child throws **Minified React error #31** ("objects are not valid as a
 * React child") and logs a console error. `friendlyError` flattens ANY error
 * shape (string detail, array detail, nested object, plain Error) into a single
 * human-readable string that is always safe to toast / render.
 */

interface ApiErrorShape {
  response?: {
    status?: number
    data?: {
      detail?: unknown
      message?: unknown
      error?: unknown
    }
  }
  message?: unknown
}

function coerceDetail(d: unknown): string {
  if (d == null) return ''
  if (typeof d === 'string') return d
  if (Array.isArray(d)) {
    return d
      .map((x) => {
        if (typeof x === 'string') return x
        const item = x as { msg?: unknown; loc?: unknown }
        if (item?.msg != null) {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter((p) => p !== 'body').join('.')
            : ''
          return loc ? `${loc}: ${String(item.msg)}` : String(item.msg)
        }
        try {
          return JSON.stringify(x)
        } catch {
          return ''
        }
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof d === 'object') {
    const item = d as { msg?: unknown; detail?: unknown }
    if (typeof item.msg === 'string') return item.msg
    if (typeof item.detail === 'string') return item.detail
    try {
      return JSON.stringify(d)
    } catch {
      return ''
    }
  }
  return String(d)
}

/**
 * Coerce any thrown API error into a readable string.
 * ALWAYS returns a string — safe to hand to `toast.*`, `description`, or JSX.
 */
export function friendlyError(err: unknown, fallback = 'Une erreur est survenue'): string {
  const e = err as ApiErrorShape
  const data = e?.response?.data
  const detail = coerceDetail(data?.detail ?? data?.message ?? data?.error)
  const msg = detail || (typeof e?.message === 'string' ? e.message : '')
  return msg || fallback
}

/** Alias — matches the name used in deep-test triage notes. */
export const friendlyDetail = friendlyError

// ── Toast helpers ─────────────────────────────────────────────────────────────
// Kept here so pages import ONE module for both error + success toasts. Uses the
// portal's sonner toaster. `toastError` always flattens the error first so we
// never hit React error #31 (rendering a raw Pydantic detail array).
import { toast } from 'sonner'

/**
 * Show a friendly sonner error toast from ANY thrown error shape and return the
 * flattened message (handy for inline field errors too).
 */
export function toastError(err: unknown, fallback = 'Une erreur est survenue'): string {
  const msg = friendlyError(err, fallback)
  toast.error(msg)
  return msg
}

/** Convenience success toast. */
export function toastSuccess(message: string, description?: string): void {
  toast.success(message, description ? { description } : undefined)
}
